import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import billingService from '../../services/billingService';
import { Check, X, CreditCard, TrendingUp, TrendingDown, ExternalLink } from 'lucide-react';

const BillingSection = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [billingInfo, setBillingInfo] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    loadBillingInfo();
  }, [user]);

  const loadBillingInfo = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const data = await billingService.getBillingInfo(user.id);
      setBillingInfo(data);
    } catch (error) {
      console.error('Error loading billing info:', error);
      setMessage({ type: 'error', text: 'Failed to load billing information' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planName) => {
    try {
      setMessage({ type: '', text: '' });
      await billingService.createCheckoutSession(user.id, planName);
      // Will redirect to Stripe Checkout
    } catch (error) {
      console.error('Error upgrading plan:', error);
      setMessage({ type: 'error', text: 'Failed to start upgrade process' });
    }
  };

  const handleManageSubscription = async () => {
    try {
      await billingService.createPortalSession(user.id);
      // Will redirect to Stripe Customer Portal
    } catch (error) {
      console.error('Error opening billing portal:', error);
      setMessage({ type: 'error', text: 'Failed to open billing portal' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  const currentPlan = billingInfo?.plan_name || 'free';
  const planDetails = billingService.getPlanDetails(currentPlan);

  const allPlans = ['free', 'pro', 'team', 'enterprise'].map(plan => ({
    ...billingService.getPlanDetails(plan),
    id: plan
  }));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Billing</h2>
        <p className="text-gray-600 mt-1">
          Manage your subscription and billing information
        </p>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`flex items-center space-x-2 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {message.type === 'success' ? <Check size={20} /> : <X size={20} />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Current Plan */}
      <div className="border border-gray-200 rounded-lg p-6 bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <h3 className="text-2xl font-bold text-gray-900">{planDetails.name} Plan</h3>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                billingInfo?.plan_status === 'active' 
                  ? 'bg-green-100 text-green-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {billingInfo?.plan_status || 'Active'}
              </span>
            </div>
            <p className="text-3xl font-bold text-purple-600 mb-4">
              {planDetails.price}
              {currentPlan !== 'free' && currentPlan !== 'enterprise' && (
                <span className="text-lg text-gray-600 font-normal">/month</span>
              )}
            </p>
            {billingInfo?.current_period_end && (
              <p className="text-sm text-gray-600">
                {billingInfo.cancel_at ? 'Cancels on' : 'Renews on'}: {new Date(billingInfo.current_period_end).toLocaleDateString()}
              </p>
            )}
          </div>
          
          {currentPlan !== 'free' && (
            <button
              onClick={handleManageSubscription}
              className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <CreditCard size={18} />
              <span>Manage Subscription</span>
              <ExternalLink size={14} />
            </button>
          )}
        </div>

        {/* Current Plan Features */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-3">Your Plan Includes:</h4>
          <ul className="space-y-2">
            {planDetails.features.map((feature, index) => (
              <li key={index} className="flex items-start space-x-2 text-sm text-gray-700">
                <Check size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Available Plans */}
      <div className="pt-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">
          {currentPlan === 'free' ? 'Upgrade Your Plan' : 'Other Plans'}
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {allPlans.map(plan => {
            const isCurrent = plan.id === currentPlan;
            const canUpgrade = 
              (currentPlan === 'free' && plan.id !== 'free') ||
              (currentPlan === 'pro' && (plan.id === 'team' || plan.id === 'enterprise')) ||
              (currentPlan === 'team' && plan.id === 'enterprise');
            const canDowngrade = 
              (currentPlan === 'enterprise' && plan.id !== 'enterprise') ||
              (currentPlan === 'team' && (plan.id === 'pro' || plan.id === 'free')) ||
              (currentPlan === 'pro' && plan.id === 'free');

            return (
              <div
                key={plan.id}
                className={`border-2 rounded-lg p-5 ${
                  isCurrent 
                    ? 'border-purple-500 bg-purple-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <h4 className="text-lg font-bold text-gray-900 mb-1">{plan.name}</h4>
                <p className="text-2xl font-bold text-purple-600 mb-4">
                  {plan.price}
                  {plan.id !== 'free' && plan.id !== 'enterprise' && (
                    <span className="text-sm text-gray-600 font-normal">/mo</span>
                  )}
                </p>
                
                <ul className="space-y-2 mb-4">
                  {plan.features.slice(0, 3).map((feature, index) => (
                    <li key={index} className="flex items-start space-x-2 text-xs text-gray-600">
                      <Check size={14} className="text-green-600 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {isCurrent ? (
                  <div className="w-full py-2 text-center text-sm font-semibold text-purple-600 bg-purple-100 rounded-lg">
                    Current Plan
                  </div>
                ) : canUpgrade ? (
                  <button
                    onClick={() => handleUpgrade(plan.id)}
                    className="w-full flex items-center justify-center space-x-2 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:from-purple-600 hover:to-blue-600 transition-all"
                  >
                    <TrendingUp size={16} />
                    <span className="text-sm font-semibold">Upgrade</span>
                  </button>
                ) : canDowngrade ? (
                  <button
                    onClick={() => handleUpgrade(plan.id)}
                    className="w-full flex items-center justify-center space-x-2 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    <TrendingDown size={16} />
                    <span className="text-sm font-semibold">Downgrade</span>
                  </button>
                ) : (
                  <button
                    disabled
                    className="w-full py-2 bg-gray-100 text-gray-400 rounded-lg cursor-not-allowed text-sm"
                  >
                    Not Available
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Billing Information */}
      {currentPlan !== 'free' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-900 mb-2">
            Need Help?
          </h4>
          <p className="text-sm text-blue-800 mb-3">
            For billing questions, invoice requests, or enterprise pricing, contact our support team.
          </p>
          <a
            href="mailto:billing@swirl.app"
            className="inline-flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            <span>Contact Billing Support</span>
            <ExternalLink size={14} />
          </a>
        </div>
      )}
    </div>
  );
};

export default BillingSection;





